import React, { useEffect, useRef, useState } from 'react';

export interface ConfirmModalProps {
  visible: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  pendingLabel?: string;
  cancelLabel?: string;
  /** Visual style of the confirm button. */
  confirmVariant?: 'primary' | 'danger';
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
}

/**
 * Generic confirmation dialog (plain React, no Syncfusion) reused across admin flows.
 * Keeps itself open and surfaces an inline error when `onConfirm` rejects; closes on success.
 */
export function ConfirmModal({
  visible,
  title,
  message,
  confirmLabel = 'Confirm',
  pendingLabel = 'Working…',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  onClose,
  onConfirm,
}: ConfirmModalProps): React.ReactElement | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setPending(false);
      setError(null);
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !pending) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [visible, onClose, pending]);

  if (!visible) {
    return null;
  }

  const handleConfirm = (): void => {
    setError(null);
    setPending(true);
    void Promise.resolve(onConfirm())
      .then(() => {
        onClose();
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Action failed.');
      })
      .finally(() => {
        setPending(false);
      });
  };

  const confirmClass = confirmVariant === 'danger' ? 'bo-btn bo-btn-danger' : 'bo-btn bo-btn-primary';

  return (
    <div
      className="bo-modal-backdrop"
      role="presentation"
      onClick={() => {
        if (!pending) {
          onClose();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !pending) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        className="bo-modal-panel"
        role="dialog"
        aria-modal
        aria-labelledby="bo-confirm-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="bo-confirm-modal-title" className="bo-modal-title">
          {title}
        </h2>
        <div style={{ marginTop: 0 }}>{message}</div>
        {error ? (
          <p className="bo-modal-error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="bo-actions" style={{ marginTop: '1.25rem', justifyContent: 'flex-end' }}>
          <button type="button" className="bo-btn bo-btn-secondary" onClick={onClose} disabled={pending}>
            {cancelLabel}
          </button>
          <button type="button" className={confirmClass} onClick={handleConfirm} disabled={pending}>
            {pending ? pendingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
